import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Paper,
  useTheme,
  alpha,
  Stack,
  Chip,
  Avatar,
  IconButton,
  Divider
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import BusinessIcon from '@mui/icons-material/Business';
import CodeIcon from '@mui/icons-material/Code';
import ChurchIcon from '@mui/icons-material/Church';
import EventIcon from '@mui/icons-material/Event';
import PeopleIcon from '@mui/icons-material/People';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import LaunchIcon from '@mui/icons-material/Launch';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import SecurityIcon from '@mui/icons-material/Security';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import StarIcon from '@mui/icons-material/Star';

const AcutisInstitucionalPage: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();

  const handleContactSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const formData = new FormData(e.target as HTMLFormElement);
    const data = Object.fromEntries(formData);
    
    const message = `Olá! Vim através do site da ACUTIS TECNOLOGIA.

*Nome:* ${data.name}
*E-mail:* ${data.email}
*Assunto:* ${data.subject}

*Mensagem:*
${data.message}`;
    
    const whatsappUrl = `https://wa.me/5598985455382?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
    
    (e.target as HTMLFormElement).reset();
    alert('Redirecionando para o WhatsApp...');
  };

  return (
    <Box>
      {/* Hero Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
          color: 'white',
          py: { xs: 8, md: 12 },
          textAlign: 'center',
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'radial-gradient(circle at 30% 20%, rgba(255,255,255,0.1) 0%, transparent 50%)',
            pointerEvents: 'none'
          }
        }}
      >
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          {/* Logo ACUTIS */}
          <Box
            sx={{
              width: 80,
              height: 80,
              mx: 'auto',
              mb: 4,
              background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
              borderRadius: '16px 16px 16px 4px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              position: 'relative',
              '&::before': {
                content: '""',
                position: 'absolute',
                bottom: 0,
                left: 0,
                width: '50%',
                height: '50%',
                background: 'white',
                borderRadius: '0 100% 0 0'
              }
            }}
          >
            <BusinessIcon sx={{ fontSize: 40, color: 'white', zIndex: 1 }} />
          </Box>
          
          <Typography 
            variant="h1" 
            component="h1" 
            gutterBottom 
            fontWeight="bold"
            sx={{
              fontSize: { xs: '2.5rem', md: '4rem' },
              mb: 3,
              textShadow: '0 4px 20px rgba(0,0,0,0.3)'
            }}
          >
            ACUTIS TECNOLOGIA LTDA
          </Typography>
          <Typography 
            variant="h4" 
            sx={{ 
              mb: 6, 
              opacity: 0.95, 
              maxWidth: 800, 
              mx: 'auto',
              fontWeight: 300,
              lineHeight: 1.4
            }}
          >
            Desenvolvemos soluções tecnológicas que facilitam a gestão de eventos, 
            comunidades e instituições. Transformação digital com foco em inovação, 
            simplicidade e eficiência.
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} justifyContent="center">
            <Button
              variant="contained"
              size="large"
              onClick={() => navigate('/produtos')}
              sx={{
                px: 6,
                py: 2,
                fontSize: '1.2rem',
                fontWeight: 'bold',
                background: 'rgba(255,255,255,0.2)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255,255,255,0.3)',
                borderRadius: 2,
                '&:hover': {
                  background: 'rgba(255,255,255,0.3)',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 8px 25px rgba(0,0,0,0.2)'
                },
                transition: 'all 0.3s ease'
              }}
            >
              <CodeIcon sx={{ mr: 1 }} />
              Conheça Nossos Produtos
            </Button>
            <Button
              variant="outlined"
              size="large"
              onClick={() => navigate('/contato')}
              sx={{
                px: 6,
                py: 2,
                fontSize: '1.2rem',
                fontWeight: 'bold',
                color: 'white',
                borderColor: 'rgba(255,255,255,0.5)',
                borderWidth: 2,
                '&:hover': {
                  borderColor: 'white',
                  background: 'rgba(255,255,255,0.1)',
                  transform: 'translateY(-2px)'
                },
                transition: 'all 0.3s ease'
              }}
            >
              <PhoneIcon sx={{ mr: 1 }} />
              Entre em Contato
            </Button>
          </Stack>
        </Container>
      </Box>

      {/* Sobre a Empresa */}
      <Container maxWidth="lg" sx={{ py: 10 }}>
        <Box textAlign="center" mb={8}>
          <Chip 
            label="SOBRE A EMPRESA" 
            sx={{ 
              mb: 3,
              background: 'linear-gradient(45deg, #1e3a8a, #3b82f6)',
              color: 'white',
              fontWeight: 'bold',
              fontSize: '0.9rem',
              px: 2,
              py: 1
            }} 
          />
          <Typography 
            variant="h2" 
            component="h2" 
            gutterBottom 
            fontWeight="bold"
            sx={{
              background: 'linear-gradient(45deg, #1f2937, #374151)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              mb: 3
            }}
          >
            Inovação, Simplicidade e Eficiência
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 600, mx: 'auto' }}>
            Em cada solução que desenvolvemos
          </Typography>
        </Box>

        <Grid container spacing={6} alignItems="center">
          <Grid item xs={12} md={6}>
            <Typography variant="h5" gutterBottom fontWeight="600" color="primary.main">
              Nossa História
            </Typography>
            <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.8, color: 'text.secondary' }}>
              A <strong style={{ color: '#1e3a8a' }}>ACUTIS TECNOLOGIA LTDA</strong> é uma empresa brasileira 
              especializada em desenvolvimento de software, aplicativos e soluções digitais. 
              Atuamos na criação de plataformas que conectam pessoas, comunidades e instituições, 
              sempre com foco em inovação, simplicidade e eficiência.
            </Typography>
            <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.8, color: 'text.secondary' }}>
              Nossa missão é democratizar o acesso à tecnologia, oferecendo ferramentas que 
              transformam a gestão de eventos e fortalecem os laços comunitários. Sediada em{' '}
              <strong style={{ color: '#1e3a8a' }}>Santa Helena - MA</strong>, nossa empresa combina 
              expertise técnica com profundo entendimento das necessidades do mercado brasileiro.
            </Typography>
          </Grid>

          {/* <Grid item xs={12} md={6}>
            <Grid container spacing={3}>
              {[
                { icon: PeopleIcon, number: '500+', label: 'Clientes Ativos', color: '#3b82f6' },
                { icon: CodeIcon, number: '2', label: 'Produtos Principais', color: '#1e40af' },
                { icon: StarIcon, number: '98%', label: 'Satisfação', color: '#60a5fa' },
                { icon: SupportAgentIcon, number: '24/7', label: 'Suporte', color: '#2563eb' }
              ].map((stat, index) => (
                <Grid item xs={6} key={stat.label}>
                  <Card 
                    sx={{ 
                      p: 3, 
                      textAlign: 'center',
                      transition: 'all 0.3s ease-in-out',
                      border: '1px solid #e2e8f0',
                      '&:hover': {
                        transform: 'translateY(-8px)',
                        boxShadow: '0 12px 40px rgba(59, 130, 246, 0.15)',
                        borderColor: '#3b82f6'
                      }
                    }}
                  >
                    <Avatar 
                      sx={{ 
                        width: 60, 
                        height: 60, 
                        mx: 'auto', 
                        mb: 2,
                        background: `linear-gradient(45deg, ${stat.color}, ${stat.color}dd)`
                      }}
                    >
                      <stat.icon sx={{ fontSize: 30 }} />
                    </Avatar>
                    <Typography variant="h3" fontWeight="bold" sx={{ color: stat.color, mb: 1 }}>
                      {stat.number}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" fontWeight="500">
                      {stat.label}
                    </Typography>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Grid> */}
        </Grid>
      </Container>

      {/* Nossos Produtos */}
      <Box sx={{ background: `linear-gradient(135deg, ${alpha('#3b82f6', 0.05)} 0%, ${alpha('#60a5fa', 0.05)} 100%)`, py: 10 }}>
        <Container maxWidth="lg">
          <Box textAlign="center" mb={8}>
            <Chip 
              label="NOSSOS PRODUTOS" 
              sx={{ 
                mb: 3,
                background: 'linear-gradient(45deg, #1e3a8a, #3b82f6)',
                color: 'white',
                fontWeight: 'bold',
                fontSize: '0.9rem',
                px: 2,
                py: 1
              }} 
            />
            <Typography 
              variant="h2" 
              component="h2" 
              gutterBottom 
              fontWeight="bold"
              sx={{
                background: 'linear-gradient(45deg, #1f2937, #374151)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                mb: 3
              }}
            >
              Soluções Digitais Inovadoras
            </Typography>
            <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 700, mx: 'auto' }}>
              Plataformas que transformam a gestão de eventos e comunidades
            </Typography>
          </Box>

          <Grid container spacing={4}>
            {[
              {
                icon: ChurchIcon,
                title: 'Meu Sacramento',
                description: 'Plataforma completa para gestão de eventos, horários de missas e atividades comunitárias. Conecta paróquias e fiéis através de uma interface moderna e intuitiva, facilitando a organização e participação em atividades religiosas.',
                features: ['Gestão de Eventos', 'Horários de Missas', 'Comunicação Direta', 'Interface Intuitiva'],
                link: 'https://meusacramento.online',
                external: true,
                color: '#1e40af'
              },
              {
                icon: EventIcon,
                title: 'Meu Sacramento',
                description: 'Plataforma robusta de gestão de eventos, inscrições e pagamentos. Oferece ferramentas completas para organização, promoção e controle de eventos de todos os tipos, com sistema integrado de vendas e relatórios detalhados.',
                features: ['Gestão Completa', 'Sistema de Pagamentos', 'Relatórios Detalhados', 'Controle de Inscrições'],
                link: '/contato',
                external: false,
                color: '#3b82f6'
              }
            ].map((product, index) => (
              <Grid item xs={12} md={6} key={product.title}>
                <Card 
                  sx={{ 
                    height: '100%',
                    transition: 'all 0.3s ease-in-out',
                    border: '1px solid #e2e8f0',
                    position: 'relative',
                    overflow: 'hidden',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: '0 12px 40px rgba(59, 130, 246, 0.15)',
                      borderColor: '#3b82f6'
                    },
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      height: 4,
                      background: `linear-gradient(90deg, ${product.color} 0%, ${product.color}aa 100%)`
                    }
                  }}
                >
                  <CardContent sx={{ p: 4 }}>
                    <Avatar 
                      sx={{ 
                        width: 70, 
                        height: 70, 
                        mb: 3,
                        background: `linear-gradient(45deg, ${product.color}, ${product.color}dd)`
                      }}
                    >
                      <product.icon sx={{ fontSize: 35 }} />
                    </Avatar>
                    
                    <Typography variant="h4" component="h3" gutterBottom fontWeight="bold" sx={{ mb: 3 }}>
                      {product.title}
                    </Typography>
                    
                    <Typography 
                      variant="body1" 
                      color="text.secondary" 
                      sx={{ mb: 4, lineHeight: 1.7 }}
                    >
                      {product.description}
                    </Typography>

                    <Box sx={{ mb: 4 }}>
                      <Typography variant="h6" gutterBottom fontWeight="600" sx={{ mb: 2 }}>
                        Recursos Inclusos:
                      </Typography>
                      <Grid container spacing={1}>
                        {product.features.map((feature) => (
                          <Grid item xs={6} key={feature}>
                            <Box display="flex" alignItems="center" sx={{ mb: 1 }}>
                              <Box 
                                sx={{ 
                                  width: 6, 
                                  height: 6, 
                                  borderRadius: '50%', 
                                  background: product.color, 
                                  mr: 1 
                                }} 
                              />
                              <Typography variant="body2" color="text.secondary">
                                {feature}
                              </Typography>
                            </Box>
                          </Grid>
                        ))}
                      </Grid>
                    </Box>
                    
                    <Button 
                      variant="contained"
                      fullWidth
                      size="large"
                      onClick={() => product.external ? window.open(product.link, '_blank') : navigate(product.link)}
                      sx={{
                        background: `linear-gradient(45deg, ${product.color}, ${product.color}dd)`,
                        py: 1.5,
                        fontWeight: 'bold',
                        '&:hover': {
                          background: `linear-gradient(45deg, ${product.color}dd, ${product.color})`
                        }
                      }}
                    >
                      {product.external ? (
                        <>
                          Acessar Plataforma <LaunchIcon sx={{ ml: 1, fontSize: 18 }} />
                        </>
                      ) : (
                        <>
                          Saiba Mais <EmailIcon sx={{ ml: 1, fontSize: 18 }} />
                        </>
                      )}
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Seção de Impacto */}
      <Container maxWidth="lg" sx={{ py: 10 }}>
        <Paper 
          elevation={0}
          sx={{ 
            p: 8, 
            textAlign: 'center',
            background: `linear-gradient(135deg, ${alpha('#3b82f6', 0.05)} 0%, ${alpha('#60a5fa', 0.05)} 100%)`,
            border: '2px solid rgba(59, 130, 246, 0.1)',
            borderRadius: 3,
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: -50,
              right: -50,
              width: 200,
              height: 200,
              background: 'radial-gradient(circle, rgba(59, 130, 246, 0.1) 0%, transparent 70%)',
              borderRadius: '50%'
            }
          }}
        >
          <TrendingUpIcon sx={{ fontSize: 80, color: '#3b82f6', mb: 4, opacity: 0.9 }} />
          <Typography variant="h3" component="h2" gutterBottom fontWeight="bold">
            Transformando o Futuro Digital
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ mb: 6, maxWidth: 700, mx: 'auto', lineHeight: 1.6 }}>
            Nossa tecnologia está revolucionando a forma como empresas e instituições 
            se conectam com suas comunidades, criando experiências mais engajadas e eficientes.
          </Typography>
          
          <Grid container spacing={6} sx={{ mt: 2 }}>
            <Grid item xs={12} md={3}>
              <SecurityIcon sx={{ fontSize: 50, color: '#3b82f6', mb: 2 }} />
              <Typography variant="h2" sx={{ color: '#3b82f6', fontWeight: 'bold', mb: 1 }}>
                100%
              </Typography>
              <Typography variant="h6" color="text.secondary" fontWeight="500">
                Segurança dos Dados
              </Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <AccessTimeIcon sx={{ fontSize: 50, color: '#3b82f6', mb: 2 }} />
              <Typography variant="h2" sx={{ color: '#3b82f6', fontWeight: 'bold', mb: 1 }}>
                60%
              </Typography>
              <Typography variant="h6" color="text.secondary" fontWeight="500">
                Economia de Tempo
              </Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <TrendingUpIcon sx={{ fontSize: 50, color: '#3b82f6', mb: 2 }} />
              <Typography variant="h2" sx={{ color: '#3b82f6', fontWeight: 'bold', mb: 1 }}>
                85%
              </Typography>
              <Typography variant="h6" color="text.secondary" fontWeight="500">
                Aumento na Eficiência
              </Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <StarIcon sx={{ fontSize: 50, color: '#3b82f6', mb: 2 }} />
              <Typography variant="h2" sx={{ color: '#3b82f6', fontWeight: 'bold', mb: 1 }}>
                98%
              </Typography>
              <Typography variant="h6" color="text.secondary" fontWeight="500">
                Satisfação dos Clientes
              </Typography>
            </Grid>
          </Grid>
        </Paper>
      </Container>

      {/* Seção de Contato */}
      <Box sx={{ background: 'linear-gradient(135deg, #1f2937 0%, #374151 100%)', color: 'white', py: 10 }}>
        <Container maxWidth="lg">
          <Box textAlign="center" mb={8}>
            <Chip 
              label="ENTRE EM CONTATO" 
              sx={{ 
                mb: 3,
                background: 'rgba(255,255,255,0.2)',
                color: 'white',
                fontWeight: 'bold',
                fontSize: '0.9rem',
                px: 2,
                py: 1
              }} 
            />
            <Typography variant="h2" component="h2" gutterBottom fontWeight="bold" color="white">
              Estamos Prontos para Ajudar
            </Typography>
            <Typography variant="h6" sx={{ opacity: 0.9, maxWidth: 600, mx: 'auto' }}>
              Transforme suas ideias em soluções digitais inovadoras
            </Typography>
          </Box>

          <Grid container spacing={6}>
            <Grid item xs={12} md={6}>
              <Stack spacing={4}>
                {[
                  {
                    icon: EmailIcon,
                    title: 'E-mail',
                    content: 'contato@meusacramento.online',
                    description: 'Para questões gerais e parcerias'
                  },
                  {
                    icon: PhoneIcon,
                    title: 'WhatsApp',
                    content: '+55 98 98545-5382',
                    description: 'Atendimento de segunda a sexta, 8h às 18h'
                  },
                  {
                    icon: LocationOnIcon,
                    title: 'Localização',
                    content: 'Santa Helena - MA, Brasil',
                    description: 'ACUTIS TECNOLOGIA LTDA'
                  }
                ].map((contact, index) => (
                  <Paper
                    key={contact.title}
                    sx={{
                      p: 3,
                      background: 'rgba(255,255,255,0.05)',
                      backdropFilter: 'blur(10px)',
                      border: '1px solid rgba(255,255,255,0.1)',
                      borderRadius: 2,
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        background: 'rgba(255,255,255,0.1)',
                        transform: 'translateY(-2px)'
                      }
                    }}
                  >
                    <Box display="flex" alignItems="center" gap={2}>
                      <Avatar 
                        sx={{ 
                          background: 'linear-gradient(45deg, #3b82f6, #60a5fa)',
                          width: 50,
                          height: 50
                        }}
                      >
                        <contact.icon />
                      </Avatar>
                      <Box>
                        <Typography variant="h6" fontWeight="600" color="white">
                          {contact.title}
                        </Typography>
                        <Typography variant="body1" color="rgba(255,255,255,0.9)" fontWeight="500">
                          {contact.content}
                        </Typography>
                        <Typography variant="body2" color="rgba(255,255,255,0.7)">
                          {contact.description}
                        </Typography>
                      </Box>
                    </Box>
                  </Paper>
                ))}
              </Stack>
            </Grid>

            <Grid item xs={12} md={6}>
              <Paper
                sx={{
                  p: 4,
                  background: 'rgba(255,255,255,0.05)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: 2
                }}
              >
                <Typography variant="h5" gutterBottom fontWeight="600" color="white" sx={{ mb: 3 }}>
                  Envie uma Mensagem
                </Typography>
                
                <Box component="form" onSubmit={handleContactSubmit}>
                  <Stack spacing={3}>
                    <Box>
                      <Typography variant="body2" color="rgba(255,255,255,0.9)" sx={{ mb: 1 }}>
                        Nome
                      </Typography>
                      <Box
                        component="input"
                        name="name"
                        required
                        placeholder="Seu nome completo"
                        sx={{
                          width: '100%',
                          p: 2,
                          background: 'rgba(255,255,255,0.1)',
                          border: '1px solid rgba(255,255,255,0.2)',
                          borderRadius: 1,
                          color: 'white',
                          fontSize: '1rem',
                          '&::placeholder': { color: 'rgba(255,255,255,0.6)' },
                          '&:focus': {
                            outline: 'none',
                            borderColor: '#3b82f6'
                          }
                        }}
                      />
                    </Box>
                    
                    <Box>
                      <Typography variant="body2" color="rgba(255,255,255,0.9)" sx={{ mb: 1 }}>
                        E-mail
                      </Typography>
                      <Box
                        component="input"
                        type="email"
                        name="email"
                        required
                        placeholder="seu@email.com"
                        sx={{
                          width: '100%',
                          p: 2,
                          background: 'rgba(255,255,255,0.1)',
                          border: '1px solid rgba(255,255,255,0.2)',
                          borderRadius: 1,
                          color: 'white',
                          fontSize: '1rem',
                          '&::placeholder': { color: 'rgba(255,255,255,0.6)' },
                          '&:focus': {
                            outline: 'none',
                            borderColor: '#3b82f6'
                          }
                        }}
                      />
                    </Box>
                    
                    <Box>
                      <Typography variant="body2" color="rgba(255,255,255,0.9)" sx={{ mb: 1 }}>
                        Assunto
                      </Typography>
                      <Box
                        component="input"
                        name="subject"
                        required
                        placeholder="Como podemos ajudar?"
                        sx={{
                          width: '100%',
                          p: 2,
                          background: 'rgba(255,255,255,0.1)',
                          border: '1px solid rgba(255,255,255,0.2)',
                          borderRadius: 1,
                          color: 'white',
                          fontSize: '1rem',
                          '&::placeholder': { color: 'rgba(255,255,255,0.6)' },
                          '&:focus': {
                            outline: 'none',
                            borderColor: '#3b82f6'
                          }
                        }}
                      />
                    </Box>
                    
                    <Box>
                      <Typography variant="body2" color="rgba(255,255,255,0.9)" sx={{ mb: 1 }}>
                        Mensagem
                      </Typography>
                      <Box
                        component="textarea"
                        name="message"
                        required
                        rows={4}
                        placeholder="Descreva seu projeto ou dúvida..."
                        sx={{
                          width: '100%',
                          p: 2,
                          background: 'rgba(255,255,255,0.1)',
                          border: '1px solid rgba(255,255,255,0.2)',
                          borderRadius: 1,
                          color: 'white',
                          fontSize: '1rem',
                          resize: 'none',
                          fontFamily: 'inherit',
                          '&::placeholder': { color: 'rgba(255,255,255,0.6)' },
                          '&:focus': {
                            outline: 'none',
                            borderColor: '#3b82f6'
                          }
                        }}
                      />
                    </Box>
                    
                    <Button 
                      type="submit" 
                      variant="contained" 
                      size="large"
                      fullWidth
                      sx={{
                        py: 2,
                        background: 'linear-gradient(45deg, #3b82f6, #60a5fa)',
                        fontWeight: 'bold',
                        fontSize: '1.1rem',
                        '&:hover': {
                          background: 'linear-gradient(45deg, #2563eb, #3b82f6)'
                        }
                      }}
                    >
                      <EmailIcon sx={{ mr: 1 }} />
                      Enviar Mensagem
                    </Button>
                  </Stack>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Footer */}
      <Box sx={{ background: '#111827', color: 'white', py: 6 }}>
        <Container maxWidth="lg">
          <Box textAlign="center">
            <Stack direction="row" spacing={4} justifyContent="center" sx={{ mb: 4 }}>
              {['Início', 'Sobre', 'Produtos', 'Contato', 'Política de Privacidade'].map((item) => (
                <Button
                  key={item}
                  onClick={() => navigate(item === 'Política de Privacidade' ? '/politica-privacidade' : `/${item.toLowerCase()}`)}
                  sx={{ 
                    color: 'rgba(255,255,255,0.7)', 
                    '&:hover': { color: '#3b82f6' },
                    textTransform: 'none'
                  }}
                >
                  {item}
                </Button>
              ))}
            </Stack>
            
            <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)', mb: 4 }} />
            
            <Typography variant="body2" color="rgba(255,255,255,0.7)">
              © 2024 ACUTIS TECNOLOGIA LTDA - Todos os direitos reservados
            </Typography>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default AcutisInstitucionalPage;

